/**
 * Role-Based Permission Utilities
 *
 * Pure functions for checking user permissions based on their role within a
 * tenant.  These functions have no Vue/Nuxt dependencies so they can be used
 * in unit tests, stores, and composables alike.
 *
 * Role hierarchy (highest → lowest):
 *   owner > admin > member > viewer
 */

export type Role = 'owner' | 'admin' | 'member' | 'viewer';

/** Numeric rank for each role – higher means more permissions. */
const ROLE_RANK: Record<Role, number> = {
    owner: 4,
    admin: 3,
    member: 2,
    viewer: 1,
};

/**
 * Returns true when the user's role meets or exceeds the required minimum role.
 *
 * @param userRole    - The role currently held by the user (null/undefined = no access).
 * @param minimumRole - The lowest role that is permitted to perform the action.
 */
export function hasMinimumRole(
    userRole: Role | string | null | undefined,
    minimumRole: Role,
): boolean {
    if (!userRole) return false;
    return (ROLE_RANK[userRole as Role] ?? 0) >= ROLE_RANK[minimumRole];
}

/**
 * Named permission checks.
 * Each function accepts the current user's role and returns a boolean.
 */
export const PERMISSIONS = {
    /** Invite new team members (admin+). */
    canInviteMembers: (role: Role | string | null | undefined): boolean =>
        hasMinimumRole(role, 'admin'),

    /** Remove existing team members (admin+). */
    canRemoveMembers: (role: Role | string | null | undefined): boolean =>
        hasMinimumRole(role, 'admin'),

    /** Change a team member's role (admin+). */
    canManageRoles: (role: Role | string | null | undefined): boolean =>
        hasMinimumRole(role, 'admin'),

    /** Create, edit, or delete content such as tasks, projects, and clients (member+). */
    canMutate: (role: Role | string | null | undefined): boolean =>
        hasMinimumRole(role, 'member'),

    /** Full team-management access: invite + remove + manage roles (admin+). */
    canManageTeam: (role: Role | string | null | undefined): boolean =>
        hasMinimumRole(role, 'admin'),

    /** Respond to client approvals (all tenant members including viewers). */
    canRespondApprovals: (role: Role | string | null | undefined): boolean =>
        hasMinimumRole(role, 'viewer'),

    /** Send messages in tenant/client threads (all tenant members including viewers). */
    canMessage: (role: Role | string | null | undefined): boolean =>
        hasMinimumRole(role, 'viewer'),
} as const;

/**
 * Roles that can be assigned to team members via the UI.
 * `owner` is intentionally excluded – ownership is not transferable through the
 * standard invite or role-change flows.
 */
export const ASSIGNABLE_ROLES = ['admin', 'member', 'viewer'] as const;
export type AssignableRole = typeof ASSIGNABLE_ROLES[number];
